// src/components/email/EmailSection.tsx

import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InboxIcon from '@mui/icons-material/Inbox';
import MailIcon from '@mui/icons-material/Mail';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Button,
  TextField,
  Menu,
  MenuItem,
  Divider,
  Badge,
  styled,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import React, { useState, useEffect } from 'react';

import EmailIntelligencePanel from '@/components/email/EmailIntelligencePanel';
import { fetchEmailAccounts } from '../../services/email/emailAccountService';
import { initiateAuth, fetchMessages, sendEmail as orchestratorSendEmail } from '../../services/email/emailOrchestrator';
import type {
  EmailAttachment,
  EmailMessage,
  EmailAccount,
  EmailProvider,
  SendMessageOptions,
} from '../../services/email/types';

// Styled components
const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: '0.75rem',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  backgroundColor: '#ffffff',
}));

const CardHeader = styled(Box)(({ theme }) => ({
  padding: '1rem 1.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  backgroundColor: '#ffffff',
}));

const CardContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  backgroundColor: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
}));

const EmailItem = styled(ListItem)<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: '0.75rem 1rem',
  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
  backgroundColor: selected ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  cursor: 'pointer',
}));

// Update the email display to handle the service's interface
const formatDate = (date: Date | string) => {
  const dateObj = date instanceof Date ? date : new Date(date);
  return dateObj.toLocaleString([], {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Update the email list item to use the correct properties
const EmailListItem = ({
  email,
  selected,
  onClick,
}: {
  email: EmailMessage;
  selected: boolean;
  onClick: () => void;
}) => (
  <EmailItem selected={selected} onClick={onClick}>
    <ListItemIcon>
      <Avatar>{email.from.charAt(0).toUpperCase()}</Avatar>
    </ListItemIcon>
    <ListItemText
      primary={email.subject}
      secondary={
        <>
          <Typography component="span" variant="body2" color="textPrimary">
            {email.from}
          </Typography>
          {' — '}
          {email.message.substring(0, 50)}...
        </>
      }
    />
    <Typography variant="caption" color="textSecondary">
      {formatDate(email.date)}
    </Typography>
  </EmailItem>
);

// Update the email detail view to use the correct properties
const EmailDetail = ({ email }: { email: EmailMessage }) => {
  const attachments: Array<{ id: string; name: string; size: number }> = Array.isArray(
    email.attachments
  )
    ? email.attachments
    : [];
  return (
    <Box p={2}>
      <Typography variant="h6">{email.subject}</Typography>
      <Typography variant="subtitle2" color="textSecondary">
        From: {email.from}
      </Typography>
      <Typography variant="subtitle2" color="textSecondary">
        To: {email.to}
      </Typography>
      <Typography variant="subtitle2" color="textSecondary">
        Date: {formatDate(email.date)}
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
        {email.message}
      </Typography>
      {attachments.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2">Attachments:</Typography>
          <List>
            {attachments.map((attachment: any, idx: number) => (
              <ListItem key={idx}>
                <ListItemIcon>
                  <AttachFileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={attachment.name}
                  secondary={(attachment.size ? (attachment.size / 1024).toFixed(1) : '0') + ' KB'}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

const EmailSection: React.FC = () => {
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [composeMode, setComposeMode] = useState(false);
  const [newEmail, setNewEmail] = useState({
    to: '',
    subject: '',
    body: '',
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [activeAccount, setActiveAccount] = useState<EmailAccount | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addAccountDialogOpen, setAddAccountDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Load accounts and emails on mount
  useEffect(() => {
    const loadAccounts = async () => {
      const savedAccounts = await fetchEmailAccounts();
      setAccounts(savedAccounts);

      const defaultAccount = savedAccounts.find(a => a.isDefault) || savedAccounts[0];
      if (defaultAccount) {
        setActiveAccount(defaultAccount);
      }
    };

    loadAccounts();
  }, []);

  // Load emails when active account changes
  useEffect(() => {
    const loadEmails = async () => {
      if (!activeAccount?.isConnected) return;

      setIsLoading(true);
      setError(null);

      try {
        const messages = await fetchMessages(activeAccount.provider);
        setEmails(messages as EmailMessage[]);
      } catch (error) {
        console.error('Error loading emails:', error);
        setError('Failed to load emails. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadEmails();
  }, [activeAccount]);

  // Count unread emails
  const unreadCount = emails.filter(email => !email.read).length;

  // Handle email selection
  const handleEmailClick = (email: EmailMessage) => {
    setSelectedEmail(email);
    setComposeMode(false);
  };

  // Handle starring/unstarring emails
  const handleStarClick = (event: React.MouseEvent, emailId: string) => {
    event.stopPropagation();
    const updatedEmails = emails.map(email =>
      email.id === emailId ? { ...email, starred: !email.starred } : email
    );
    setEmails(updatedEmails);
  };

  // Handle compose button click
  const handleComposeClick = () => {
    setSelectedEmail(null);
    setComposeMode(true);
    setNewEmail({
      to: '',
      subject: '',
      body: '',
    });
  };

  // Handle sending new email
const handleSendEmail = async () => {
    if (!activeAccount) {
      setError('No active email account selected');
      return;
    }

    setSendingEmail(true);

    try {
      const payload: SendMessageOptions = {
        to: newEmail.to ? [newEmail.to] : [],
        subject: newEmail.subject,
        body: newEmail.body,
      };

      await orchestratorSendEmail(activeAccount.provider, payload);
      alert('Email sent successfully!');
      setComposeMode(false);
    } catch (error) {
      console.error('Error sending email:', error);
      setError('An error occurred while sending the email.');
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle menu open
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle account menu
  const handleAccountMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  // Handle folder change
  const handleFolderChange = (folder: string) => {
    setActiveFolder(folder);
    setSelectedEmail(null);
    setComposeMode(false);
  };

  // Handle account selection
  const handleAccountSelect = (account: EmailAccount) => {
    setActiveAccount(account);
    setAccountMenuAnchor(null);
  };

  // Handle add account
  const handleAddAccount = (provider: EmailProvider) => {
    setAddAccountDialogOpen(false);
    initiateAuth(provider);
  };

  // Filter emails based on active folder
  const filteredEmails = emails.filter((email: EmailMessage) => {
    if (activeFolder === 'starred') return email.starred;
    if (activeFolder === 'sent') return false; // In a real app, you'd have sent emails
    return true; // inbox shows all for this demo
  });

  return (
    <StyledCard>
      <CardHeader>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="medium" color="text.primary">
            Email
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            sx={{ ml: 1 }}
            onClick={() => {
              if (activeAccount) {
                setIsLoading(true);
                fetchMessages(activeAccount.provider)
                  .then((messages: EmailMessage[]) => {
                    setEmails(messages);
                    setIsLoading(false);
                  })
                  .catch((error: any) => {
                    console.error('Error refreshing emails:', error);
                    setError('Failed to refresh emails');
                    setIsLoading(false);
                  });
              }
            }}
            disabled={isLoading || !activeAccount}
          >
            {isLoading ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" sx={{ ml: 1 }} onClick={handleMenuClick}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={handleMenuClose}>Mark all as read</MenuItem>
            <MenuItem onClick={handleMenuClose}>Settings</MenuItem>
          </Menu>
        </Box>
      </CardHeader>

      <CardContent>
        {error && (
          <Box
            sx={{
              p: 2,
              bgcolor: 'error.light',
              color: 'error.dark',
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}

        {!activeAccount ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3,
            }}
          >
            <Typography variant="h6" gutterBottom>
              No Email Account Connected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              Connect an email account to view and send emails directly from the dashboard.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddAccountDialogOpen(true)}
            >
              Connect Email Account
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                  },
                }}
              >
                <Tab icon={<MailIcon />} label="Email" iconPosition="start" />
                <Tab icon={<SmartToyIcon />} label="AI Intelligence" iconPosition="start" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 ? (
              // Email Tab
              <Box sx={{ display: 'flex', height: '100%' }}>
                {/* Email sidebar */}
                <Box
                  sx={{
                    width: 200,
                    borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#3B82F6',
                        '&:hover': {
                          backgroundColor: '#2563EB',
                        },
                      }}
                      onClick={handleComposeClick}
                    >
                      Compose
                    </Button>
                  </Box>

                  <List sx={{ px: 1 }}>
                    <ListItem
                      button
                      selected={activeFolder === 'inbox'}
                      onClick={() => handleFolderChange('inbox')}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          color: '#3B82F6',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Badge badgeContent={unreadCount} color="primary">
                          <InboxIcon fontSize="small" />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText primary="Inbox" />
                    </ListItem>

                    <ListItem
                      button
                      selected={activeFolder === 'starred'}
                      onClick={() => handleFolderChange('starred')}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          color: '#3B82F6',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <StarIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Starred" />
                    </ListItem>

                    <ListItem
                      button
                      selected={activeFolder === 'sent'}
                      onClick={() => handleFolderChange('sent')}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          color: '#3B82F6',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <SendOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Sent" />
                    </ListItem>

                    <ListItem
                      button
                      selected={activeFolder === 'drafts'}
                      onClick={() => handleFolderChange('drafts')}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          color: '#3B82F6',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <DraftsOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Drafts" />
                    </ListItem>

                    <ListItem
                      button
                      selected={activeFolder === 'trash'}
                      onClick={() => handleFolderChange('trash')}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          color: '#3B82F6',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Trash" />
                    </ListItem>
                  </List>

                  <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={handleAccountMenuClick}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          mr: 1,
                          bgcolor:
                            activeAccount?.provider === 'gmail'
                              ? '#DB4437'
                              : activeAccount?.provider === 'outlook'
                                ? '#0078D4'
                                : activeAccount?.provider === 'yahoo'
                                  ? '#6001D2'
                                  : '#f59e0b',
                        }}
                      >
                        {activeAccount?.provider.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {activeAccount?.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {activeAccount?.provider}
                        </Typography>
                      </Box>
                      <ExpandMoreIcon fontSize="small" />
                    </Box>

                    <Menu
                      anchorEl={accountMenuAnchor}
                      open={Boolean(accountMenuAnchor)}
                      onClose={handleAccountMenuClose}
                      PaperProps={{
                        sx: { minWidth: 200 },
                      }}
                    >
                      {accounts.map(account => (
                        <MenuItem
                          key={account.id}
                          onClick={() => handleAccountSelect(account)}
                          selected={activeAccount?.id === account.id}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                mr: 1,
                                bgcolor:
                                  account.provider === 'gmail'
                                    ? '#DB4437'
                                    : account.provider === 'outlook'
                                      ? '#0078D4'
                                      : account.provider === 'yahoo'
                                        ? '#6001D2'
                                        : '#f59e0b',
                              }}
                            >
                              {account.provider.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2">{account.email}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                      <Divider />
                      <MenuItem onClick={() => setAddAccountDialogOpen(true)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AddIcon fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="body2">Add account</Typography>
                        </Box>
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>

                {/* Email list and content */}
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {isLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : composeMode ? (
                    // Compose email form
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Typography variant="h6" gutterBottom>
                        New Message
                      </Typography>

                      <TextField
                        label="To"
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="normal"
                        value={newEmail.to}
                        onChange={e => setNewEmail({ ...newEmail, to: e.target.value })}
                      />

                      <TextField
                        label="Subject"
                        variant="outlined"
                        size="small"
                        fullWidth
                        margin="normal"
                        value={newEmail.subject}
                        onChange={e => setNewEmail({ ...newEmail, subject: e.target.value })}
                      />

                      <TextField
                        label="Message"
                        variant="outlined"
                        multiline
                        rows={8}
                        fullWidth
                        margin="normal"
                        value={newEmail.body}
                        onChange={e => setNewEmail({ ...newEmail, body: e.target.value })}
                        sx={{ flexGrow: 1 }}
                      />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Button
                          startIcon={<AttachFileIcon />}
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                        >
                          Attach
                        </Button>

                        <Button
                          startIcon={sendingEmail ? <CircularProgress size={20} /> : <SendIcon />}
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            backgroundColor: '#3B82F6',
                            '&:hover': {
                              backgroundColor: '#2563EB',
                            },
                          }}
                          onClick={handleSendEmail}
                          disabled={sendingEmail || !newEmail.to || !newEmail.subject}
                        >
                          Send
                        </Button>
                      </Box>
                    </Box>
                  ) : selectedEmail ? (
                    // Email detail view
                    <EmailDetail email={selectedEmail} />
                  ) : (
                    // Email list view
                    <List sx={{ p: 0, height: '100%', overflow: 'auto' }}>
                      {filteredEmails.length > 0 ? (
                        filteredEmails.map((email: EmailMessage, idx: number) => (
                          <EmailListItem
                            key={email.id || idx}
                            email={email}
                            selected={
                              selectedEmail !== null &&
                              (selectedEmail as EmailMessage).id === email.id
                            }
                            onClick={() => handleEmailClick(email)}
                          />
                        ))
                      ) : (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography color="text.secondary">No emails found</Typography>
                        </Box>
                      )}
                    </List>
                  )}
                </Box>
              </Box>
            ) : (
              // AI Intelligence Tab
              <Box sx={{ height: '100%', overflow: 'auto' }}>
                <EmailIntelligencePanel />
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      {/* Add Account Dialog */}
      <Dialog
        open={addAccountDialogOpen}
        onClose={() => setAddAccountDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Connect Email Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select your email provider to connect your account:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={
                <Box
                  component="img"
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/7e/Gmail_icon_%282020%29.svg/2560px-Gmail_icon_%282020%29.svg.png"
                  sx={{ width: 24, height: 24 }}
                />
              }
              onClick={() => handleAddAccount('gmail')}
              sx={{ justifyContent: 'flex-start', py: 1 }}
            >
              Connect Gmail Account
            </Button>

            <Button
              variant="outlined"
              startIcon={
                <Box
                  component="img"
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg/1200px-Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg.png"
                  sx={{ width: 24, height: 24 }}
                />
              }
              onClick={() => handleAddAccount('outlook')}
              sx={{ justifyContent: 'flex-start', py: 1 }}
            >
              Connect Outlook Account
            </Button>

            <Button
              variant="outlined"
              startIcon={
                <Box
                  component="img"
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/7c/Yahoo%21_purple_logo.svg/2560px-Yahoo%21_purple_logo.svg.png"
                  sx={{ width: 24, height: 24 }}
                />
              }
              onClick={() => handleAddAccount('yahoo')}
              sx={{ justifyContent: 'flex-start', py: 1 }}
            >
              Connect Yahoo Mail
            </Button>

            <Button
              variant="outlined"
              startIcon={<MailIcon />}
              onClick={() => handleAddAccount('exchange')}
              sx={{ justifyContent: 'flex-start', py: 1 }}
            >
              Connect Company Email
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddAccountDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </StyledCard>
  );
};

export default EmailSection;
